import { all, call, put, select } from 'redux-saga/effects'
import { MutationOptions, QueryOptions } from '@apollo/client'
import { refreshKeycloakToken } from 'src/helpers/KeycloakHelpers'

import client, { defaultConfig, persistedClient } from '../../graphql/client'
import bugsnagClient from '../../helpers/BugsnagHelpers'
import { actions, selectors } from '..'
import { refreshToken } from '../../graphql/Services/User/mutations'

export type ApiTransformer = (data: any) => any

export default class ApiSagas {
  static *checkToken() {
    const jwt = yield select(selectors.user.jwt)
    const isRefreshingToken = yield select(selectors.api.isRefreshingToken)
    if (jwt && !isRefreshingToken) {
      const remainingSeconds = jwt.exp - new Date().getTime() / 1000

      if (remainingSeconds < 60 * 15) {
        console.log('token needs refreshing', {
          jwt,
          expires: new Date(jwt.exp * 1000),
        })
        yield put(actions.api.setIsRefreshingToken(true))
        const isUsingKeycloak = !!(yield select(selectors.user.keycloakToken))

        if (isUsingKeycloak) {
          const keyCloakToken = yield refreshKeycloakToken()
          yield put(actions.user.setKeycloakToken(keyCloakToken))
          yield put(actions.api.setIsRefreshingToken(false))

          return
        }

        const results = yield call(ApiSagas.mutate, refreshToken())
        yield put(actions.user.setToken(results?.data))
        yield put(actions.api.setIsRefreshingToken(false))
      }
    }
  }

  static *getHeaders(): any {
    const headers = yield select(selectors.api.headers)
    yield call(ApiSagas.checkToken)

    const token = yield select(selectors.user.token)
    return {
      ...headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    }
  }

  static *query(service: {
    query: QueryOptions
    transformer?: (data: any) => any
  }) {
    return yield ApiSagas.call(
      client.query,
      service.query,
      service?.transformer
    )
  }

  static *persistQuery(service: {
    query: QueryOptions
    transformer?: (data: any) => any
  }) {
    return yield ApiSagas.call(
      persistedClient.query,
      service.query,
      service?.transformer
    )
  }

  static *mutate(service: {
    mutation: MutationOptions
    transformer?: (data: any) => any
  }) {
    return yield ApiSagas.call(
      client.mutate,
      service.mutation,
      service?.transformer
    )
  }

  static *call(method: any, service: any, transformer?: (data: any) => any) {
    const headers = yield call(ApiSagas.getHeaders)

    let result
    try {
      result = yield call(method, {
        ...defaultConfig,
        ...service,
        context: {
          ...service?.context,
          headers: {
            ...service?.context?.headers,
            ...headers,
          },
        },
      })
    } catch (e) {
      console.error(`ApiSagas:`, e, service?.variables)

      if (e?.message === 'Unauthenticated.') {
        yield put(actions.user.setUser(null))
      }

      if (bugsnagClient) {
        bugsnagClient.addMetadata('graphQL', {
          Variables: service?.variables,
          Config: service,
        })
        bugsnagClient.notify(e)
      }

      return {
        errors: e,
      }
    }

    if (result.errors) {
      console.error(`ApiSagas:`, result.errors)
    }

    return yield call(
      ApiSagas.transform,
      result,
      transformer || service?.transformer
    )
  }

  static *transform(result: any, transformer?: ApiTransformer) {
    if (!result.data || !transformer) {
      return result
    }

    const data = yield call(transformer, result.data as any)

    return { ...result, data }
  }

  static *loop() {
    yield all([
      //
    ])
  }
}
